import React from "react";
import { GoldDivider } from "@/components/luxury";

export default function WhatAVerifiedKennelEarns() {
  return (
    <>
      <p className="font-spectral italic text-xl md:text-2xl tracking-tight text-[#3a3a3a] leading-snug max-w-2xl">
        The Verified tier isn't an upgrade in the SaaS sense. It's a
        promise we co-sign with you to every buyer who finds your
        kennel through us.
      </p>

      <GoldDivider variant="ornament" className="max-w-md my-10" />

      <p>
        We get this question a lot from kennels considering the
        Verified upgrade: <em>"What does it actually do for me, in
        concrete terms?"</em> Here's the full list, in writing, with
        no marketing fog.
      </p>

      <h2>1. The gold WaxSeal, on every surface</h2>
      <p>
        The platform's only earned status symbol. Once it's on your
        kennel record, the seal renders next to your name in five
        places that buyers actively read:
      </p>
      <ul>
        <li>
          The Marketplace listing card on every dog you publish.
        </li>
        <li>
          The Breeders directory card.
        </li>
        <li>
          Your public kennel profile (<code>/k/&lt;slug&gt;</code>) at
          headline scale.
        </li>
        <li>
          The Dashboard "Kennels to explore" widget that surfaces to
          every Buyer Pass holder on the platform.
        </li>
        <li>
          Anywhere your kennel is referenced in another dog's
          ownership chain (sire, dam, transferred-from).
        </li>
      </ul>
      <p>
        The seal is a single recognisable mark. Buyers learn what it
        means within their first session on the platform. After that,
        you don't have to introduce yourself.
      </p>

      <h2>2. Search-result priority — but not by paying for it</h2>
      <p>
        Verified kennels surface above unverified kennels in the
        Marketplace and Breeders search results when all other ranking
        signals are equal. <em>Equal</em> is doing a lot of work in that
        sentence: a relevant unverified kennel that matches the
        buyer's breed + location filter still beats an unrelated
        verified kennel. We don't sell ranking. The seal earns you a
        tiebreaker, not a top-of-list bypass.
      </p>
      <p>
        We make this rule public on purpose. Buyers can read it, and
        they trust the rest of the search results more for knowing it.
      </p>

      <h2>3. A downloadable badge for your own website</h2>
      <p>
        We generate a high-resolution PNG of the WaxSeal in two
        variants — gold-on-white and gold-on-black — and we host the
        canonical "Verified by Armstrong K9" link target. Drop the
        badge on your site, link it back to your platform profile,
        and any visitor who clicks lands on your verified profile
        with the seal visible. It's free brand distribution for both
        of us.
      </p>
      <p>
        We don't license the seal to non-verified kennels under any
        circumstance. If someone's claiming to be Armstrong-verified
        and isn't on our verified list, they're misrepresenting; we'll
        send a polite cease-and-desist on your behalf.
      </p>

      <h2>4. Litter waitlist deposits, with Stripe escrow</h2>
      <p>
        <em>Coming later in 2026.</em> Verified kennels will be the
        first to get the waitlist deposit feature: take a
        non-refundable $200–$500 deposit on a future litter, held in
        Stripe escrow, automatically refunded if the litter doesn't
        land within an agreed window. Removes the cancellation drama
        from your inbox and gives you a real demand signal before the
        breeding decision. Verified kennels onboard a quarter ahead of
        unverified.
      </p>

      <h2>5. Live video tour booking — with revenue split</h2>
      <p>
        15-minute Daily.co video tours from your kennel profile. The
        tour booking carries a flat $35 fee (paid by the buyer) that
        splits 90/10 in your favour — Stripe Connect transfers the
        seller's share automatically once the tour is paid. A kennel
        doing four tours a week clears about $4,500 a year just from
        due-diligence sessions that would have been free phone calls
        otherwise. Verified kennels get the tour booking widget on
        their public profile by default. Sellers must complete Stripe
        Connect onboarding before splits can be paid; until then, your
        share accrues and is released when you finish onboarding.
      </p>

      <h2>6. Direct line to admin for transfers and disputes</h2>
      <p>
        Verified kennel transfers (when you sell a dog and the
        ownership record updates) get a real human in the loop instead
        of a help-desk template. If you're caught in a payment dispute
        or a buyer-side dispute, a real person at Armstrong K9 mediates
        the conversation and can release escrow, refund a deposit, or
        revoke a transfer with cause.
      </p>

      <GoldDivider variant="ornament" className="max-w-md my-10" />

      <h2>What it costs</h2>
      <p>
        The Verified Kennel tier (also called the Elite plan) is{" "}
        <strong>$129/month or $1,290/year</strong>. The two-year prepay
        ($2,499) carries an additional Founding Kennel badge on your
        profile. Cancel anytime — the seal is revoked at the end of
        your paid period, but no records, transfers, or earnings are
        clawed back. There is no separate verification fee on top of
        the subscription.
      </p>

      <h2>What it's not</h2>
      <p>
        It's not an endorsement of any specific breeding decision. The
        seal means we verified the seven things in our{" "}
        <a href="/journal/how-we-verify-a-kennel" className="text-ink underline underline-offset-4 hover:text-crimson">
          review process
        </a>{" "}
        and that those things stayed true. If a verified kennel makes a
        breeding decision a buyer disagrees with, that's a kennel-buyer
        conversation, not a failure of verification.
      </p>
      <p>
        It's also not a permanent mark. If anything in the original
        review changes materially — a new owner, a new address, a
        complaint we couldn't reconcile — the seal pauses pending
        review. Most kennels never hit this; the ones who do tend to
        be ones whose buyers were going to find out anyway.
      </p>

      <GoldDivider variant="ornament" className="max-w-md my-10" />

      <p>
        If you're a serious kennel and the verification process sounds
        reasonable to you, you're already most of the way there. The
        rest is a Stripe checkout and a 48-hour review window. The
        seal goes live the moment we click confirm.
      </p>
      <p>
        Start your verification from the{" "}
        <a href="/billing" className="text-ink underline underline-offset-4 hover:text-crimson">
          Billing page
        </a>{" "}
        — Verified Kennel tier.
      </p>
    </>
  );
}
